// sidebar-layout.component.ts

import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  NgZone,
  HostListener,
  AfterViewInit,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import {
  ActivatedRoute,
  Data,
  Router,
  NavigationEnd,
  RouterModule,
} from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { TocNavService } from '@services/toc-nav.service';
import { ScrollStateService } from '@services/scroll-state.service';

@Component({
  selector: 'app-sidebar-layout',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar-layout.component.html',
  styleUrls: ['./sidebar-layout.component.scss'],
})
export class SidebarLayoutComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild('layout', { static: true }) layoutRef!: ElementRef<HTMLDivElement>;
  @ViewChild('mainScroller', { static: true })
  mainScrollerRef!: ElementRef<HTMLElement>;

  @ViewChild('tocSheet', { static: true })
  tocSheetRef!: ElementRef<HTMLElement>;

  @ViewChild('toggleBtn') toggleBtnRef?: ElementRef<HTMLButtonElement>;
  @ViewChild('drawerCloseBtn')
  drawerCloseBtnRef?: ElementRef<HTMLButtonElement>;

  @ViewChild('desktopHint')
  desktopHintRef?: ElementRef<HTMLDivElement>;

  sidebarType: string | null = null;

  readonly MIN_SIDEBAR = 140;
  readonly MAX_SIDEBAR = 420;
  readonly HANDLE_WIDTH = 5;
  readonly DEFAULT_SIDEBAR = 250; // px
  readonly STORAGE_KEY = 'sidebarWidth';

  sidebarWidth = 250;
  dragging = false;
  isSidebarOpen = false;
  isIntroPage = false;

  /** Tracks current viewport mode so template/CSS stay coherent. */
  isDesktopView = true;

  /** Mobile hint: always shown on intro page when Browse pill is visible. */
  showBrowseHint = false;

  /** Desktop hint (intro page only). */
  showDesktopTocHint = false;

  private moveHandler?: (e: MouseEvent | TouchEvent) => void;
  private upHandler?: (e: MouseEvent | TouchEvent) => void;

  private routeDataSub?: Subscription;
  private tocSub?: Subscription;
  private navSub?: Subscription;

  private readonly isBrowser: boolean;

  private mq?: MediaQueryList;
  private mqHandler?: (e: MediaQueryListEvent) => void;

  private hintRafId: number | null = null;
  private hintRetryTimer: number | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private zone: NgZone,
    private tocNav: TocNavService,
    private scrollState: ScrollStateService,
    @Inject(PLATFORM_ID) pid: Object,
  ) {
    this.isBrowser = isPlatformBrowser(pid);
  }

  ngOnInit() {
    if (this.isBrowser && typeof window.matchMedia === 'function') {
      this.mq = window.matchMedia('(min-width: 801px)');
      this.isDesktopView = this.mq.matches;

      this.mqHandler = (e: MediaQueryListEvent) => {
        this.zone.run(() => this.onViewportChanged(e.matches));
      };

      if (typeof this.mq.addEventListener === 'function') {
        this.mq.addEventListener('change', this.mqHandler);
      } else {
        // eslint-disable-next-line deprecation/deprecation
        this.mq.addListener(this.mqHandler as any);
      }
    } else {
      this.isDesktopView = true;
    }

    // restore saved width in browser only
    if (this.isBrowser) {
      const raw = localStorage.getItem(this.STORAGE_KEY);

      if (raw === null) {
        try {
          localStorage.setItem(this.STORAGE_KEY, String(this.sidebarWidth));
        } catch {}
      } else {
        const trimmed = raw.trim();
        if (trimmed !== '') {
          const v = Number(trimmed);
          if (Number.isFinite(v)) this.sidebarWidth = this.clamp(v);
        }
      }
    }

    this.routeDataSub = this.route.data.subscribe((data: Data) => {
      this.sidebarType = data['sidebar'] || null;
    });

    this.tocSub = this.tocNav.linkActivated$.subscribe(() => {
      if (!this.isDesktopView) this.closeSidebar();
    });

    if (this.isBrowser) {
      this.navSub = this.router.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe(() => this.updateHintsForRoute());
    }

    this.updateHintsForRoute();
  }

  ngAfterViewInit() {
    this.scrollState.setFrom(this.mainScrollerRef?.nativeElement ?? null);

    if (this.isBrowser) this.scheduleDesktopHintPosition();
  }

  ngOnDestroy() {
    this.teardownDragListeners();

    this.routeDataSub?.unsubscribe();
    this.tocSub?.unsubscribe();
    this.navSub?.unsubscribe();

    this.setBodyScrollLocked(false);

    if (this.hintRafId != null) cancelAnimationFrame(this.hintRafId);
    if (this.hintRetryTimer != null) window.clearTimeout(this.hintRetryTimer);

    if (this.mq && this.mqHandler) {
      if (typeof this.mq.removeEventListener === 'function') {
        this.mq.removeEventListener('change', this.mqHandler);
      } else {
        // eslint-disable-next-line deprecation/deprecation
        this.mq.removeListener(this.mqHandler as any);
      }
    }
  }

  get gridTemplateColumns(): string {
    return `${this.sidebarWidth}px ${this.HANDLE_WIDTH}px 1fr`;
  }

  onMainScroll(el: HTMLElement) {
    this.scrollState.setFrom(el);
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape' && this.isSidebarOpen && !this.isDesktopView) {
      this.closeSidebar();
    }
  }

  toggleSidebar() {
    if (this.isDesktopView) return;
    if (this.isSidebarOpen) this.closeSidebar();
    else this.openSidebar();
  }

  openSidebar() {
    if (this.isDesktopView) return;
    this.isSidebarOpen = true;
    this.setBodyScrollLocked(true);

    this.tocNav.notifyOpened();

    if (this.isBrowser) {
      queueMicrotask(() => this.drawerCloseBtnRef?.nativeElement?.focus());
    }
  }

  closeSidebar() {
    if (!this.isSidebarOpen) return;
    this.isSidebarOpen = false;
    this.setBodyScrollLocked(false);

    this.updateHintsForRoute();

    if (this.isBrowser) {
      queueMicrotask(() => this.toggleBtnRef?.nativeElement?.focus());
    }
  }

  private onViewportChanged(isDesktop: boolean) {
    this.isDesktopView = isDesktop;

    if (isDesktop) {
      this.isSidebarOpen = false;
      this.setBodyScrollLocked(false);
    }

    this.updateHintsForRoute();
  }

  private updateHintsForRoute(): void {
    if (!this.isBrowser) {
      this.showBrowseHint = false;
      this.showDesktopTocHint = false;
      return;
    }

    const url = (this.router.url ?? '').split('?')[0].split('#')[0];
    const onIntroPage = url === '/' || url === '' || url.startsWith('/intro');
    this.isIntroPage = onIntroPage;

    if (!onIntroPage) {
      this.showBrowseHint = false;
      this.showDesktopTocHint = false;
      return;
    }

    if (this.isDesktopView) {
      this.showDesktopTocHint = true;
      this.showBrowseHint = false;
      this.scheduleDesktopHintPosition();
      return;
    }

    this.showDesktopTocHint = false;
    this.showBrowseHint = true;
  }

  private scheduleDesktopHintPosition(tries: number = 10): void {
    if (!this.isBrowser) return;
    if (!this.isDesktopView || !this.showDesktopTocHint) return;

    if (this.hintRafId != null) cancelAnimationFrame(this.hintRafId);
    if (this.hintRetryTimer != null) window.clearTimeout(this.hintRetryTimer);

    this.hintRafId = requestAnimationFrame(() => {
      const ok = this.positionDesktopHintToFirstEntry();

      if (!ok && tries > 0) {
        this.hintRetryTimer = window.setTimeout(() => {
          this.scheduleDesktopHintPosition(tries - 1);
        }, 60);
      }
    });
  }

  private positionDesktopHintToFirstEntry(): boolean {
    const layoutEl = this.layoutRef?.nativeElement;
    const tocEl = this.tocSheetRef?.nativeElement;
    if (!layoutEl || !tocEl) return false;

    const anchor = this.findFirstTocEntry(tocEl);
    if (!anchor) return false;

    const layoutRect = layoutEl.getBoundingClientRect();
    const aRect = anchor.getBoundingClientRect();

    // If it's not laid out/visible yet, try again via the existing retry loop.
    if (
      (aRect.height === 0 && aRect.width === 0) ||
      !Number.isFinite(aRect.top)
    )
      return false;

    let centerY = aRect.top + aRect.height / 2 - layoutRect.top;

    const hintEl = this.desktopHintRef?.nativeElement;
    if (hintEl) {
      const h = hintEl.getBoundingClientRect().height || 0;
      const pad = 10;
      const minY = pad + h / 2;
      const maxY = layoutRect.height - pad - h / 2;
      centerY = Math.max(minY, Math.min(maxY, centerY));
    }

    layoutEl.style.setProperty(
      '--toc-hint-center-y',
      `${Math.round(centerY)}px`,
    );
    return true;
  }

  private findFirstTocEntry(root: HTMLElement): HTMLElement | null {
    // Prefer the actual TOC root if present.
    const tocRoot = root.querySelector<HTMLElement>('.toc') ?? root;

    // 1) Best signal: first anchor that points to an entry route.
    const entryLink = tocRoot.querySelector<HTMLElement>(
      'a[href*="/entries/"],a[href*="entries/"]',
    );
    if (entryLink) return entryLink;

    // 2) Next: first “entry-like” button/link inside the first list of items (often where entries live).
    const lists = Array.from(tocRoot.querySelectorAll<HTMLElement>('ul,ol'));
    for (const list of lists) {
      if (list.closest('.toc-search,[role="search"]')) continue;

      const firstClickable = list.querySelector<HTMLElement>(
        'a,button,[role="link"]',
      );
      if (!firstClickable) continue;

      const txt = (firstClickable.textContent ?? '').trim();
      if (txt.length >= 2) return firstClickable;
    }

    // 3) Heuristic: first clickable that doesn't look like navigation chrome (module/group headings, search, etc.).
    const candidates = Array.from(
      tocRoot.querySelectorAll<HTMLElement>('a[href],button,[role="link"]'),
    );

    for (const el of candidates) {
      if (el.closest('.toc-search,[role="search"],.sheet-header')) continue;

      const txt = (el.textContent ?? '').trim();
      if (txt.length < 2) continue;

      const low = txt.toLowerCase();
      if (
        low === 'step 1' ||
        low === 'table of contents' ||
        low === 'search' ||
        low === 'browse'
      ) {
        continue;
      }

      // If your module/group headings are uppercase, this helps avoid selecting them.
      if (txt === txt.toUpperCase() && txt.replace(/[^A-Z]/g, '').length >= 6)
        continue;

      return el;
    }

    // 4) Final fallback (keeps the hint functional even if entries are not yet present):
    return this.findHeadingByText(tocRoot, 'biochemistry');
  }

  private findHeadingByText(
    root: HTMLElement,
    targetLower: string,
  ): HTMLElement | null {
    const candidates = root.querySelectorAll<HTMLElement>(
      'h1,h2,h3,h4,h5,h6,button,[role="heading"],.toc *',
    );

    for (const el of candidates) {
      const txt = (el.textContent ?? '').trim().toLowerCase();
      if (txt === targetLower) return el;
    }

    return null;
  }

  onDragStart(evt: MouseEvent | TouchEvent) {
    if (!this.isDesktopView) return;
    if (evt instanceof MouseEvent && (evt.detail ?? 1) > 1) return;
    evt.preventDefault();
    this.dragging = true;

    if (this.isBrowser) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    }

    this.zone.runOutsideAngular(() => {
      this.moveHandler = (e) => this.onDragMove(e);
      this.upHandler = (e) => this.onDragEnd(e);
      window.addEventListener('mousemove', this.moveHandler as any, {
        passive: false,
      });
      window.addEventListener('mouseup', this.upHandler as any, {
        passive: false,
      });
      window.addEventListener('touchmove', this.moveHandler as any, {
        passive: false,
      });
      window.addEventListener('touchend', this.upHandler as any, {
        passive: false,
      });
      window.addEventListener('touchcancel', this.upHandler as any, {
        passive: false,
      });
    });
  }

  private onDragMove(evt: MouseEvent | TouchEvent) {
    const clientX = this.getClientX(evt);
    if (clientX == null) return;
    const rect = this.layoutRef.nativeElement.getBoundingClientRect();
    const proposed = Math.round(clientX - rect.left);
    const clamped = this.clamp(proposed);
    this.zone.run(() => {
      this.sidebarWidth = clamped;
    });
  }

  private onDragEnd(_: MouseEvent | TouchEvent) {
    this.dragging = false;
    if (this.isBrowser) {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      try {
        localStorage.setItem(this.STORAGE_KEY, String(this.sidebarWidth));
      } catch {}
    }
    this.teardownDragListeners();
  }

  resetSidebarWidth(): void {
    if (!this.isDesktopView) return;
    this.teardownDragListeners();
    this.dragging = false;
    this.sidebarWidth = this.DEFAULT_SIDEBAR;
    if (this.isBrowser) {
      try {
        localStorage.setItem(this.STORAGE_KEY, String(this.sidebarWidth));
      } catch {}
    }
  }

  private clamp(n: number) {
    return Math.max(this.MIN_SIDEBAR, Math.min(this.MAX_SIDEBAR, n));
  }

  private getClientX(evt: MouseEvent | TouchEvent): number | null {
    const t = evt as TouchEvent;
    if (t.touches?.length) return t.touches[0].clientX;
    if (t.changedTouches?.length) return t.changedTouches[0].clientX;
    return (evt as MouseEvent).clientX ?? null;
  }

  private teardownDragListeners() {
    if (!this.isBrowser) return;
    if (this.moveHandler) {
      window.removeEventListener('mousemove', this.moveHandler as any);
      window.removeEventListener('touchmove', this.moveHandler as any);
      this.moveHandler = undefined;
    }
    if (this.upHandler) {
      window.removeEventListener('mouseup', this.upHandler as any);
      window.removeEventListener('touchend', this.upHandler as any);
      window.removeEventListener('touchcancel', this.upHandler as any);
      this.upHandler = undefined;
    }
  }

  private setBodyScrollLocked(locked: boolean) {
    if (!this.isBrowser) return;
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    document.body.style.overflow = locked ? 'hidden' : '';
  }
}
